import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";

/**
 * The product configurator's data (0071, docs/product-configurator/design.md):
 * colours and their spellings, colour schemes, sister parts, the model grid,
 * and the part and BOM cleanup.
 */

export type ColourAliasKind = "FINISH" | "NAME";

export interface ColourDTO {
  id: number;
  /** Its key and the finish its virtual panels carry (BITTER NOCE). */
  code: string;
  name_fr: string | null;
  name_en: string | null;
  /** The two digits its SKUs end with (88). */
  sku_code: string | null;
  is_active: boolean;
  note: string | null;
  aliases: Array<{ kind: ColourAliasKind; value: string }>;
  part_count?: number;
  product_count?: number;
}

export class ColourClient extends ResourceClient<ColourDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/colour");
  }

  seed(dryRun: boolean): Promise<ApplyReportDTO> {
    return this.http.post<ApplyReportDTO>(`${this.path}/seed`, { dry_run: dryRun });
  }

  /** Every family's sizes against its colours: a product where one exists (any status), a gap where none does. */
  models(): Promise<ModelGridDTO> {
    return this.http.get<ModelGridDTO>(`${this.path}/models`);
  }

  feasibility(productId: number, primaryColourId: number, accentColourId: number | null): Promise<FeasibilityDTO> {
    const query: Record<string, string> = { product_id: String(productId), primary_colour_id: String(primaryColourId) };
    if (accentColourId != null) query.accent_colour_id = String(accentColourId);
    return this.http.get<FeasibilityDTO>(`${this.path}/models/feasibility`, { query });
  }
}

export interface ColourSchemeDTO {
  id: number;
  /** The products' COLOR value (Brown Oak). */
  sales_name: string;
  primary_colour_id: number | null;
  accent_colour_id: number | null;
  note: string | null;
  primary_code?: string | null;
  accent_code?: string | null;
  product_count?: number;
}

export class ColourSchemeClient extends ResourceClient<ColourSchemeDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/colour-scheme");
  }
}

export type ColourGroupKind = "PANEL" | "EDGEBAND" | "HARDWARE" | "PIECE" | "OTHER";
export const COLOUR_GROUP_KINDS: ColourGroupKind[] = ["PANEL", "EDGEBAND", "HARDWARE", "PIECE", "OTHER"];

export interface PartColourGroupDTO {
  id: number;
  kind: ColourGroupKind;
  name: string;
  note: string | null;
  member_count?: number;
  /** Its members' colour codes, comma separated. */
  colours?: string | null;
}

export interface ColourPartRef {
  id: number;
  number: string;
  name: string;
  status: string;
  colour_id: number | null;
  colour_code: string | null;
  colour_group_id: number | null;
}

export interface SisterDTO {
  colour_id: number;
  colour_code: string;
  colour_name: string | null;
  part: ColourPartRef | null;
}

export interface SisterSuggestionDTO {
  part: ColourPartRef;
  reason: string;
  group_id: number | null;
  group_name: string | null;
}

export interface PartColoursDTO {
  part: ColourPartRef;
  is_colour_neutral: boolean;
  group: { id: number; kind: ColourGroupKind; name: string } | null;
  /** Every member, a colour's active part first; a colour has one active part at most. */
  sisters: SisterDTO[];
  /** The colours products are sold in that have no active part in the group. */
  missing: SisterDTO[];
  suggestions: SisterSuggestionDTO[];
}

export interface CoverageDTO {
  colours: Array<{ id: number; code: string; name_fr: string | null; sku_code: string | null; is_active: boolean }>;
  /** Colour id to its members: the active part first, retired ones after. */
  groups: Array<{ id: number; kind: ColourGroupKind; name: string; members: Record<string, ColourPartRef[]> }>;
  unlinked: ColourPartRef[];
  uncoloured: ColourPartRef[];
}

export class PartColourGroupClient extends ResourceClient<PartColourGroupDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part-colour-group");
  }

  ofPart(partId: number): Promise<PartColoursDTO> {
    return this.http.get<PartColoursDTO>(`${this.path}/part/${String(partId)}`);
  }

  setColour(partId: number, colourId: number | null, neutral: boolean): Promise<PartColoursDTO> {
    return this.http.put<PartColoursDTO>(`${this.path}/part/${String(partId)}/colour`, { colour_id: colourId, is_colour_neutral: neutral });
  }

  link(partId: number, otherId: number): Promise<PartColoursDTO> {
    return this.http.post<PartColoursDTO>(`${this.path}/part/${String(partId)}/link`, { part_id: otherId });
  }

  unlink(partId: number): Promise<PartColoursDTO> {
    return this.http.post<PartColoursDTO>(`${this.path}/part/${String(partId)}/unlink`, {});
  }

  coverage(kind: ColourGroupKind): Promise<CoverageDTO> {
    return this.http.get<CoverageDTO>(`${this.path}/coverage`, { query: { kind } });
  }
}

// ── models ────────────────────────────────────────────────────────────────

export interface ModelColumnDTO {
  sales_name: string;
  scheme_id: number | null;
  primary_colour_id: number | null;
  primary_code: string | null;
  accent_colour_id: number | null;
  accent_code: string | null;
}

export interface ModelCellDTO {
  size: string;
  colour: string;
  product_id: number;
  sku: string;
  status: string;
  name: string | null;
}

export interface ModelFamilyDTO {
  variation_group: number;
  name: string;
  base: string;
  sizes: string[];
  colours: ModelColumnDTO[];
  cells: ModelCellDTO[];
  gaps: number;
}

export interface ModelGridDTO {
  families: ModelFamilyDTO[];
  unplaced: Array<{ product_id: number; sku: string; name: string | null; variation_group: number; missing: string }>;
  models: number;
  cells: number;
  gaps: number;
}

export type NeedState = "SAME" | "EXISTS" | "INACTIVE" | "MISSING" | "NOT_LINKED";

export interface NeedDTO {
  role: "PIECE" | "PANEL" | "EDGEBAND";
  part_id: number;
  part_number: string;
  part_name: string;
  colour_code: string | null;
  target_code: string | null;
  sister_id: number | null;
  sister_number: string | null;
  state: NeedState;
}

export interface FeasibilityDTO {
  reference_product_id: number;
  reference_sku: string;
  needs: NeedDTO[];
  pieces_to_make: number;
  blocking: number;
}

// ── cleanup ───────────────────────────────────────────────────────────────

export type CleanupKind = "FINISH_DUPLICATE" | "PIECE_NAME" | "BAG" | "GUIDE";
export const CLEANUP_KINDS: CleanupKind[] = ["FINISH_DUPLICATE", "PIECE_NAME", "BAG", "GUIDE"];

export interface CleanupLineDTO {
  part_id: number;
  number: string;
  name: string;
  quantity: number;
}

export interface CleanupItemDTO {
  part_id: number | null;
  number: string;
  name: string;
  note: string | null;
  lines: CleanupLineDTO[];
  users: number;
}

export interface CleanupSuggestionDTO {
  kind: CleanupKind;
  subject: string;
  title: string;
  detail: string;
  proposed: string | null;
  items: CleanupItemDTO[];
  generic: CleanupItemDTO | null;
  left_out: CleanupItemDTO[];
}

export interface ApplyReportDTO {
  dry_run: boolean;
  counts: Record<string, number>;
  problems: string[];
}

export type CleanupRuleKind = "NAME" | "CONSOLIDATION" | "REFUSED";

export interface CleanupRuleDTO {
  id: number;
  kind: CleanupRuleKind;
  part_id: number;
  part_number: string;
  part_name: string;
  target_id: number | null;
  target_number: string | null;
  value: string | null;
  reason: string | null;
  created_at: string;
}

export interface PartCorrectionDTO {
  id: number;
  part_id: number;
  name: string;
  reason: string | null;
  part_number?: string;
  current_name?: string;
}

export class CleanupClient extends ResourceClient<PartCorrectionDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/cleanup");
  }

  suggestions(kind?: CleanupKind): Promise<CleanupSuggestionDTO[]> {
    return this.http.get<CleanupSuggestionDTO[]>(`${this.path}/suggestions`, { query: kind ? { kind } : {} });
  }

  accept(kind: CleanupKind, subject: string, options: { keep?: string; name?: string } = {}): Promise<ApplyReportDTO> {
    return this.http.post<ApplyReportDTO>(`${this.path}/accept`, { kind, subject, keep: options.keep ?? null, name: options.name ?? null });
  }

  refuse(kind: CleanupKind, subject: string, note?: string): Promise<void> {
    return this.http.post<void>(`${this.path}/refuse`, { kind, subject, note: note ?? null }, { responseType: "void" });
  }

  rules(): Promise<CleanupRuleDTO[]> {
    return this.http.get<CleanupRuleDTO[]>(`${this.path}/rules`);
  }

  dropRule(kind: CleanupRuleKind, id: number): Promise<void> {
    return this.http.delete<void>(`${this.path}/rules/${kind}/${String(id)}`, { responseType: "void" });
  }

  apply(dryRun: boolean): Promise<ApplyReportDTO> {
    return this.http.post<ApplyReportDTO>(`${this.path}/apply`, { dry_run: dryRun });
  }
}

// ── product sync ──────────────────────────────────────────────────────────

/** What a sync brings over (docs/product-configurator/sync.md §2). */
export interface SyncOptions {
  fabrication: boolean;
  made_parts: boolean;
  /** A new revision takes the reference's routing; off, it keeps the target's own and only lines are compared. */
  routing: boolean;
  versions: boolean;
  create_parts: boolean;
  create_product: boolean;
  product_data: boolean;
  drawings: boolean;
  /** The new revisions of existing parts become active; off, they wait as drafts. */
  activate: boolean;
}

export const DEFAULT_SYNC_OPTIONS: SyncOptions = {
  fabrication: true,
  made_parts: true,
  routing: true,
  versions: true,
  create_parts: true,
  create_product: true,
  product_data: false,
  drawings: true,
  activate: false,
};

export interface SyncReferenceDTO {
  product_id: number;
  sku: string;
  name: string | null;
  colour: string | null;
  primary_code: string | null;
  accent_code: string | null;
  size: string | null;
  semi_finished: string | null;
  versions: string[];
  /** Why nothing can be synced from it. */
  problem: string | null;
}

/** A colour of the model: an existing product, or a missing colour with the SKU it would get. */
export interface SyncTargetDTO {
  product_id: number | null;
  scheme_id: number | null;
  sku: string | null;
  name: string | null;
  colour: string | null;
  primary_code: string | null;
  accent_code: string | null;
  status: string | null;
  /** The family sells the colour (in another size). */
  in_family: boolean;
  problem: string | null;
  /** A missing colour: how near it is to being made. Null for an existing product. */
  readiness: SyncReadiness | null;
  /** The materials it lacks, number and name. */
  missing: string[];
  /** Blockers the target's own parts answer: links to make. */
  links: number;
}

/** READY: can be created; NEEDS_LINKS: only sisters to link; MISSING_MATERIALS: a bought material is not in the colour; NOT_SET: no part colours. */
export type SyncReadiness = "READY" | "NEEDS_LINKS" | "MISSING_MATERIALS" | "NOT_SET";

export interface SyncTargetsDTO {
  reference: SyncReferenceDTO;
  targets: SyncTargetDTO[];
  /** Product colours the family does not sell, left out unless all were asked. */
  others: number;
}

export interface SyncTargetRequest {
  product_id?: number | null;
  scheme_id?: number | null;
  sku?: string | null;
  /** A creation's key to the number typed for it. */
  numbers?: Record<string, string>;
}

export interface SyncRequest {
  product_id: number;
  targets: SyncTargetRequest[];
  options: SyncOptions;
}

export type SyncBlockReason = "NO_SISTER" | "INACTIVE_SISTER" | "NOT_LINKED" | "NO_COLOUR" | "CREATE_OFF";

export interface SyncBlockerDTO {
  part_id: number;
  number: string;
  name: string;
  colour_code: string | null;
  target_colour_id: number | null;
  target_code: string | null;
  group_id: number | null;
  used_in: string | null;
  reason: SyncBlockReason;
  /** The target's own answer: what its counterpart uses in the same place. */
  suggestion: SyncSuggestionDTO | null;
}

export interface SyncSuggestionDTO {
  part_id: number;
  number: string;
  name: string;
  /** It has no colour yet: taking it gives it the target colour. */
  needs_colour: boolean;
  /** The target's part it was seen in (its sister piece). */
  seen_in: string;
  /** On that edge (TOP, LEFT...); null: a line of its bill. */
  edge: string | null;
}

export type SyncCreationKind = "PRODUCT" | "SEMI_FINISHED" | "VERSION" | "PIECE" | "MADE";

export interface SyncCreationDTO {
  key: string;
  kind: SyncCreationKind;
  source_number: string;
  number: string | null;
  name: string | null;
  editable: boolean;
  problem: string | null;
}

export interface SyncLineChangeDTO {
  change: "ADDED" | "REMOVED" | "CHANGED";
  number: string;
  name: string | null;
  box: number | null;
  from: number | null;
  to: number | null;
}

export type SyncChangeKind = "BILL" | "PART" | "BOXES" | "PACKING" | "PRODUCT_DATA" | "VERSION_MISSING";

export interface SyncChangeDTO {
  kind: SyncChangeKind;
  part_id: number | null;
  number: string;
  lines: SyncLineChangeDTO[];
  details: string[];
}

export interface SyncTargetPlanDTO {
  product_id: number | null;
  scheme_id: number | null;
  sku: string | null;
  colour: string | null;
  blocking: SyncBlockerDTO[];
  creations: SyncCreationDTO[];
  changes: SyncChangeDTO[];
  in_sync: boolean;
  notes: string[];
  problem: string | null;
}

export interface SyncCheckDTO {
  reference: SyncReferenceDTO;
  targets: SyncTargetPlanDTO[];
}

export interface SyncWrittenDTO {
  kind: string;
  id: number;
  number: string;
}

export interface SyncTargetResultDTO {
  product_id: number | null;
  sku: string | null;
  colour: string | null;
  done: boolean;
  message: string | null;
  revisions: SyncWrittenDTO[];
  created: SyncWrittenDTO[];
  updated: SyncWrittenDTO[];
  to_do: string[];
}

export interface SyncRunReportDTO {
  run_id: number;
  /** When, to the minute: the label of every revision it wrote. */
  at: string;
  targets: SyncTargetResultDTO[];
}

export interface ProductSyncRunDTO {
  id: number;
  reference_product_id: number;
  reference_sku: string | null;
  options: SyncRequest;
  report: SyncRunReportDTO;
  account_name: string | null;
  created_at: string;
}

export class ProductSyncClient extends ResourceClient<ProductSyncRunDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/product-sync");
  }

  /** The model's colours; with all, every product colour, not only the family's (slower: each is planned). */
  targets(productId: number, all = false): Promise<SyncTargetsDTO> {
    return this.http.get<SyncTargetsDTO>(`${this.path}/targets/${String(productId)}`, { query: { all: String(all) } });
  }

  check(request: SyncRequest): Promise<SyncCheckDTO> {
    return this.http.post<SyncCheckDTO>(`${this.path}/check`, request);
  }

  run(request: SyncRequest): Promise<SyncRunReportDTO> {
    return this.http.post<SyncRunReportDTO>(`${this.path}/run`, request);
  }

  /** Takes a blocker's suggestion: the sister given the colour when it has none, then linked. */
  adopt(partId: number, sisterId: number, colourId: number): Promise<void> {
    return this.http.post<void>(`${this.path}/adopt`, { part_id: partId, sister_id: sisterId, colour_id: colourId }, { responseType: "void" });
  }
}
